import type { Database } from 'sql.js';
import {
  queryOne,
  rowFirstKeyColumn as col,
  sqlPositiveIntId,
  type SqlJsBind,
} from '@core/infrastructure/driven/sqljs/sqljsSqlitePrimitives.ts';
import type { RedencionBonoParticipanteFormPayload } from '../../../domain/redencionBonoModel.ts';
import { participanteRowToForm } from './mappers.ts';

/** Finds the redemption ID associated with a given participant ID. */
export function findRedencionIdForParticipanteInDb(db: Database, participanteId: number): number | null {
  const row = queryOne(
    db,
    'SELECT redencion_bono_id AS rid FROM redencion_bono_participante WHERE participante_id = ? LIMIT 1',
    [participanteId],
  );
  return sqlPositiveIntId(col(row ?? {}, 'rid', 'RID'));
}

/** Another redemption id when the participant would duplicate entrega + proveedor, or null. */
export function findConflictingRedencionIdForParticipanteInDb(
  db: Database,
  participanteId: number,
  entregaCatalogId: number,
  proveedorCatalogId: number,
  excludedRedencionId: number | null,
): number | null {
  if (!Number.isFinite(participanteId) || participanteId <= 0) return null;
  if (!Number.isFinite(entregaCatalogId) || entregaCatalogId <= 0) return null;
  if (!Number.isFinite(proveedorCatalogId) || proveedorCatalogId <= 0) return null;
  const row = queryOne(
    db,
    `SELECT rbp.redencion_bono_id AS rid
     FROM redencion_bono_participante rbp
     INNER JOIN redenciones_bono r ON rbp.redencion_bono_id = r.id
     WHERE rbp.participante_id = ? AND r.entrega_id = ? AND r.proveedor_id = ?
       AND (? IS NULL OR rbp.redencion_bono_id != ?)
     LIMIT 1`,
    [participanteId, entregaCatalogId, proveedorCatalogId, excludedRedencionId, excludedRedencionId],
  );
  return sqlPositiveIntId(col(row ?? {}, 'rid', 'RID'));
}

/** Lists all participants for a specific redemption. */
export function listParticipantesForRedencionInDb(
  db: Database,
  redencionId: number,
  filesPublicPrefix: string,
): RedencionBonoParticipanteFormPayload[] {
  const st = db.prepare(`
    SELECT rbp.*, p.nombre AS nombre_participante, p.telefono, p.celular, b.nombre AS bono_nombre
    FROM redencion_bono_participante rbp
    LEFT JOIN participantes p ON p.id = rbp.participante_id
    LEFT JOIN bonos b ON b.id = rbp.bono_id
    WHERE rbp.redencion_bono_id = ?
    ORDER BY rbp.id
  `);
  const rows: Record<string, SqlJsBind>[] = [];
  try {
    st.bind([redencionId]);
    while (st.step()) {
      rows.push(st.getAsObject() as Record<string, SqlJsBind>);
    }
  } finally {
    st.free();
  }
  return rows.map((r) => participanteRowToForm(db, r, filesPublicPrefix));
}
